import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import {
    Button, 
    Card, 
    CardActions, 
    CardContent,
    CardHeader,
    Divider,
    FormControl,
    InputLabel,
    OutlinedInput,
    Checkbox,
    FormControlLabel,
    Stack,
    Tooltip,
    FormHelperText,
    Select,
    MenuItem,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Agent } from '../../../types/UserTypes';
import { Document } from '../../../types/DocumentType';
import axios from '../../../../services/ApiService';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../../../../hooks/useDocuments';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';

function AdminAgentCreate() {
  const navigate = useNavigate();
  const { saveDocument } = useDocuments();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationComment, setVerificationComment] = useState('');
  const [companies, setCompanies] = useState<Map<number, string>>(new Map());
  const [agentData, setAgentData] = useState<Agent>({
    agentId: 0,
    userId: 0,
    companyId: 0,
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    contact: '',
    companyName: '',
    isPrimary: false,
    isVerified: false,
    verificationComment: '',
    address: '',
    status: BVTConstants.ACTIVE,
    certId: 0,
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });

  const [certificateData, setCertificateData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        
        const companiesResponse = await axios.get('/getCompanyMap');
        if (companiesResponse.data) {
          const companyMap = new Map(
            Object.entries(companiesResponse.data).map(([key, value]) => [Number(key), String(value)])
          );
          setCompanies(companyMap);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    if (name === 'isVerified') {
      setIsVerified(checked);
    } else if (name === 'companyId') {
      setAgentData(prev => ({
        ...prev,
        companyId: Number(value)
      }));
    } else {
    setAgentData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    let newErrors: { [key: string]: string } = {};

    if (!agentData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!agentData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!agentData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(agentData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!agentData.contact.trim()) {
      newErrors.contact = 'Phone number is required';
    } else if (agentData.contact.trim()) {
      if (!BVTConstants.PHONE_VALIDATION_PATTERN.test(agentData.contact.trim())) {
        newErrors.contact = 'Please enter a valid phone number';
      }
      if (agentData.contact.trim().length < BVTConstants.PHONE_MIN_LENGTH || 
          agentData.contact.trim().length > BVTConstants.PHONE_MAX_LENGTH) {
        newErrors.contact = `Phone number must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} characters`;
      }
    }
    if (!agentData.companyId) {
      newErrors.companyId = 'Company is required';
    }
    if (!agentData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (isVerified) {
      if (!verificationComment.trim()) {
        newErrors.verificationComment = 'Verification comment is required when verified';
      }
      if (!certificateData.contentFile) {
        newErrors.certificate = 'Certificate is required when verified';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
        const response = await axios.post("/createAgent", agentData);
        if(response.data && response.data.agentId > 0 && isVerified){
          const docId = await saveDocument(certificateData);
          console.log('Document saved successfully ' + docId);
          agentData.agentId = response.data.agentId;
          agentData.userId = response.data.userId;
          agentData.certId = docId;
          agentData.isVerified = true;
          agentData.verificationComment = verificationComment;
          await axios.post("/updateAgentProfile", agentData);
        }
        console.log('Agent saved successfully ' + response.data.agentId);
        toast.success("Agent saved Successfully.");
        navigate(-1);
    } catch (error) {
        console.error('Error saving agent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet><title>BVT Admin - Agents</title></Helmet>
    <Stack spacing={3}>
        <form
        onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
        }}
        >
        <Card>
            <CardHeader subheader="Add a New Agent" title="New Agent" />
            <Divider />
            <CardContent>
            <Grid container spacing={3}>
                <Grid xs={12} sx={{ mt: -2, mb: -1, marginLeft: 1 }} container justifyContent="space-between">
                    <FormControl>
                      <Tooltip title="Verify this agent">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isVerified}
                                    onChange={handleChange}
                                />
                            }
                            label="Verified"
                            name="isVerified"
                          />
                        </Tooltip>
                    </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                    <FormControl fullWidth required error={!!errors.firstName}>
                        <InputLabel>First name</InputLabel>
                        <OutlinedInput 
                            label="First name" 
                            name="firstName"
                            value={agentData.firstName}
                            onChange={handleChange}
                            inputProps={{ maxLength: 25 }}
                        />
                        {errors.firstName && (
                            <FormHelperText error>{errors.firstName}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                    <FormControl fullWidth required error={!!errors.lastName}>
                        <InputLabel>Last name</InputLabel>
                        <OutlinedInput 
                            label="Last name" 
                            name="lastName"
                            value={agentData.lastName}
                            onChange={handleChange}
                            inputProps={{ maxLength: 25 }}
                        />
                        {errors.lastName && (
                            <FormHelperText error>{errors.lastName}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                    <FormControl fullWidth required error={!!errors.email}>
                        <InputLabel>Email address</InputLabel>
                        <OutlinedInput 
                            label="Email address" 
                            name="email"
                            value={agentData.email}
                            onChange={handleChange}
                            inputProps={{ maxLength: 40 }}
                        />
                        {errors.email && (
                            <FormHelperText error>{errors.email}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                    <FormControl fullWidth required error={!!errors.contact}>
                        <InputLabel>Phone number</InputLabel>
                        <OutlinedInput 
                            label="Phone number" 
                            name="contact"
                            type="tel"
                            value={agentData.contact}
                            onChange={(e) => {
                                const value = e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, '');
                                setAgentData(prev => ({
                                    ...prev,
                                    contact: value
                                }));
                                if (errors.contact) {
                                    setErrors(prev => ({ ...prev, contact: '' }));
                                }
                            }}
                            inputProps={{ maxLength: BVTConstants.PHONE_MAX_LENGTH }}
                            placeholder="10-15 digits"
                        />
                        {errors.contact && (
                            <FormHelperText error>{errors.contact}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.companyId}>
                    <InputLabel>Company</InputLabel>
                    <Select 
                      label="Company" 
                      name="companyId"
                      value={agentData.companyId.toString()}
                      onChange={handleChange}
                    >
                      {Array.from(companies.entries()).map(([id, name]) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.companyId && (
                      <FormHelperText error>{errors.companyId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                    <FormControl fullWidth required error={!!errors.address}>
                        <InputLabel>Address</InputLabel>
                        <OutlinedInput 
                            label="Address" 
                            name="address"
                            value={agentData.address}
                            onChange={handleChange}
                            inputProps={{ maxLength: 150 }}
                        />
                        {errors.address && (
                            <FormHelperText error>{errors.address}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                {isVerified && (
                    <Grid container spacing={2} xs={12}>
                        <Grid xs={9}>
                            <FormControl fullWidth error={!!errors.verificationComment}>
                                <InputLabel>Verification Comment</InputLabel>
                                <OutlinedInput 
                                    label="Verification Comment" 
                                    name="verificationComment"
                                    value={verificationComment}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={(event) => setVerificationComment(event.target.value)}
                                />
                                {errors.verificationComment && (
                                    <FormHelperText error>{errors.verificationComment}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid xs={3}>
                            <Button
                                variant="contained"
                                component="label"
                                fullWidth
                            >
                                {certificateData.fileName ? 'Change File' : 'Upload Certificate'}
                                <input
                                    type="file"
                                    hidden
                                    accept={BVTConstants.FILE_TYPES}
                                    onChange={(event) => {
                                        if (event.target.files && event.target.files.length > 0) {
                                            const file = event.target.files[0];
                                            
                                            // Check file size
                                            if (file.size > BVTConstants.FILE_SIZE_500_KB_LIMIT) {
                                                setErrors(prev => ({ ...prev, certificate: 'File size should not exceed 500 KB' }));
                                                return;
                                            }
                                            
                                            // Check file format
                                            const allowedTypes = BVTConstants.FILE_TYPES.split(',');
                                            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                                            const isValidFormat = allowedTypes.some(type => 
                                                type.toLowerCase() === fileExtension
                                            );
                                            
                                            if (!isValidFormat) {
                                                setErrors(prev => ({ ...prev, certificate: `Invalid file format. Allowed formats: ${BVTConstants.FILE_TYPES}` }));
                                                return;
                                            }
                                            
                                            setCertificateData(prev => ({
                                                ...prev,
                                                contentFile: file,
                                                fileName: file.name,
                                                purpose: BVTConstants.AGENT_CERTIFICATE
                                            }));
                                            setErrors(prev => ({ ...prev, certificate: '' }));
                                        }
                                    }}
                                />
                            </Button>
                            {certificateData.fileName && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        display: 'block', 
                                        mt: 1, 
                                        wordBreak: 'break-word',
                                        color: 'text.secondary'
                                    }}
                                >
                                    File Selected: {certificateData.fileName}
                                </Typography>
                            )}
                            {errors.certificate && (
                                    <FormHelperText error>{errors.certificate}</FormHelperText>
                                )}
                        </Grid>
                    </Grid>
                )}
            </Grid>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
                <Button variant="contained" type="submit">Create</Button>
            </CardActions>
        </Card>
        <LoadingSpinner loading={loading} />
        </form>
    </Stack>
    </>
  )
}
export default AdminAgentCreate